/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ChangeEvent, memo, useMemo } from 'react';
import { FormProvider, RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from '@components/error/src/error-message';
import { LabelPrimary } from '@components/label';
import { getNestedFormError } from '@utils/get-nested-form-error/get-nested-form-error';
import classNames from 'classnames';

import { DynamicInputFieldsProps, Field, FieldWrapperProps } from './dynamic-input-fields.type';
import {
  createOptions,
  FIXED_FIELDS,
  generateLabel,
  generateRegisterOptions,
  getComponentFieldBy,
  getPlaceholderText,
} from './utils';

const FieldWrapper = ({
  field,
  children,
  label,
  parentFormField,
  showLabel = true,
  showError = true,
}: FieldWrapperProps) => {
  const fieldName = parentFormField ? `${parentFormField}.${field.slug}` : field.slug;
  return (
    <>
      {showLabel && <LabelPrimary htmlFor={fieldName}>{label}</LabelPrimary>}
      {children}
      {showError && <ErrorMessage name={fieldName} className='mt-1' />}
    </>
  );
};

export const DynamicInputFields = memo(
  ({
    fields,
    companyCountries,
    venueIsoCode,
    vatCountryCode,
    gridClasses,
    optionalFields,
    parentFormField,
    customOrder,
  }: DynamicInputFieldsProps) => {
    const { t, i18n } = useTranslation();
    const methods = useFormContext();
    const {
      formState: { errors },
      register,
      setValue,
      watch,
    } = methods;

    const fieldsOrder = customOrder ?? FIXED_FIELDS;
    const createFieldName = (field: Field) => (parentFormField ? `${parentFormField}.${field.slug}` : field.slug);

    const sortFunction = (a: Field, b: Field) => {
      const indexA = fieldsOrder.indexOf(a.slug);
      const indexB = fieldsOrder.indexOf(b.slug);

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    };

    const fieldsToComponents = useMemo(
      () =>
        fields
          .sort((a, b) => sortFunction(a, b))
          .map((field) => {
            const options = createOptions({ min: field.min, max: field.max, options: field.options });
            const fieldName = createFieldName(field);
            const { extraProps, name: component } = getComponentFieldBy({
              fieldName: field.dataType,
              venueIsoCode,
              countries: companyCountries,
              options,
              t,
            });
            const label = generateLabel(field, t, i18n, optionalFields);
            const placeholderText = getPlaceholderText(field, t, i18n);
            const registerOptions: RegisterOptions = generateRegisterOptions(field, t, optionalFields);
            const fieldError = getNestedFormError(errors, fieldName);
            const props = {
              placeholder: placeholderText,
              hasError: !!fieldError,
              error: fieldError?.message,
              ...register(fieldName, registerOptions),
              name: fieldName,
              id: fieldName,
              disabled: field.disabled,
              readOnly: field.readOnly,
              required: optionalFields ? false : field.isRequired,
              ...extraProps,
              ...field.extraProps,
              ...(field.dataType === 'vat' && {
                country: vatCountryCode,
                value: watch(fieldName),
              }),
              ...(field.dataType === 'iban' && { value: watch(fieldName) }),
              ...(field.dataType === 'numeric' && { value: watch(fieldName) }),
            };

            return {
              ...field,
              component,
              props,
              label,
              showWrapperLabel: extraProps.showWrapperLabel,
              showWrapperError: extraProps.showWrapperError,
            };
          }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [errors, i18n, venueIsoCode, fields, vatCountryCode, companyCountries, optionalFields]
    );

    const handleMultipleChange = (event: boolean | ChangeEvent, fieldName: string, value: string) => {
      if (typeof event === 'boolean') {
        setValue(fieldName, value, {
          shouldTouch: true,
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    };

    return (
      <div
        className={classNames(
          'grid gap-4 items-start mb-2 sm:gap-6 sm:mb-8',
          {
            'md:grid-cols-2': !gridClasses,
          },
          gridClasses as string
        )}>
        <FormProvider {...methods}>
          {fieldsToComponents.map((field) => {
            const ComponentName = field.component;
            const fieldName = createFieldName(field);
            return (
              <div key={field.id} data-testid='dynamic-input-field'>
                <FieldWrapper
                  field={field}
                  label={field.label}
                  parentFormField={parentFormField}
                  showLabel={field.showWrapperLabel as boolean}
                  showError={field.showWrapperError as boolean}>
                  {field.dataType === 'radio' || field.dataType === 'checkbox' ? (
                    field.options?.map((option, index) => (
                      <ComponentName
                        key={`${field.slug}-${index}`}
                        {...field.props}
                        value={option.value}
                        label={option.label}
                        onChange={(e: boolean | ChangeEvent) =>
                          handleMultipleChange(e, `${fieldName}.${index}`, option.value)
                        }
                      />
                    ))
                  ) : (
                    <ComponentName
                      {...field.props}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        setValue(fieldName, value, {
                          shouldTouch: true,
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  )}
                </FieldWrapper>
              </div>
            );
          })}
        </FormProvider>
      </div>
    );
  }
);
